import {useMutation} from "@apollo/client";
import pluralize from "pluralize";
import toast from "react-hot-toast";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {GQL_BULK_DELETE_MAINTENANCE_SCHEDULES, GQL_MAINTENANCE_SCHEDULES_LIST} from "@/lib/queries/maintenance.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {MaintenanceScheduleListItem} from "@/lib/types/maintenance.ts";
import {MaintenanceItemList} from "@/pages/maintenance/maintenance-dashboard/components/MaintenanceItemList/MaintenanceItemList.tsx";

export interface BulkDeleteSchedulesDialogProps {
  schedules: MaintenanceScheduleListItem[];
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkDeleteSchedulesDialog = ({schedules, show, onClose, onSuccess}: BulkDeleteSchedulesDialogProps) => {
  const [bulkDeleteSchedules, {reset, loading}] = useMutation(GQL_BULK_DELETE_MAINTENANCE_SCHEDULES, {
    refetchQueries: [GQL_MAINTENANCE_SCHEDULES_LIST],
  });

  const count = schedules.length;

  const handleConfirm = async () => {
    const response = await bulkDeleteSchedules({
      variables: {ids: schedules.map((s) => s.id)},
    });
    if (response.data?.response?.status === ResponseStatus.SUCCESS) {
      onSuccess();
      onClose();
    } else {
      toast.error(response.data?.response?.message || "Failed to delete maintenance schedules.");
    }
  };

  return (
    <DeleteActionDialog
      show={show}
      loading={loading}
      title="Delete Maintenance Schedules"
      onConfirm={handleConfirm}
      size="lg"
      onCancel={() => {
        onClose();
        reset();
      }}
    >
      Are you sure you want to permanently delete{" "}
      <span className="pulseTextBodyBold">{pluralize("selected schedule", count, true)}</span>? This will remove them
      from all linked checks.
      <MaintenanceItemList items={schedules} />
    </DeleteActionDialog>
  );
};
